<template>
  <div class="login-content">
    <div class="ibf-h1-larger login-title">
      {{ $t('login.loginForm.title') }}
    </div>
    <div class="login-subtitle">
      {{ $t('login.loginForm.subtitle') }}
    </div>

    <q-form
      ref="login-form"
      @submit="submitLogin"
    >
      <div class="login-form">
        <div class="login-input-fields">
          <div class="login-input-field">
            <label
              class="login-label"
              for="username"
              >{{ $t('login.loginForm.username.text') }}</label
            >
            <q-input
              ref="username-field"
              v-model="username"
              autocomplete="username"
              no-error-icon
              :error="!!getLoginError"
              dense
              tabindex="1"
              type="text"
              input-class="brand_almost_black"
              bg-color="white"
              :placeholder="$t('login.loginForm.username.placeholder')"
              stack-label
              outlined
              lazy-rules
              @clear="formAutofill = false"
              @change="formAutofill = false"
              @animationstart="checkAutoFill"
              @keydown="isUsernameError = false"
            >
              <template v-slot:prepend>
                <q-icon class="uil uil-user login-input-icon" />
              </template>
              <template #append>
                <q-icon
                  v-show="username"
                  class="cursor-pointer uil uil-times icon-hover"
                  @click="username = ''"
                />
              </template>
            </q-input>
          </div>
          <div
            class="login-input-field"
            :class="isAzureEnabled ? 'login-input-field-azure' : ''"
            style="margin-top: 48px"
          >
            <label
              class="login-label"
              for="password"
              >{{ $t('login.loginForm.password.text') }}</label
            >
            <q-input
              ref="password-field"
              v-model="password"
              style="padding-bottom: 0"
              :input-style="showPass ? { 'text-overflow': 'ellipsis', overflow: 'hidden', 'white-space': 'nowrap' } : ''"
              no-error-icon
              :error="!!getLoginError"
              dense
              autocomplete="password"
              tabindex="2"
              :type="showPass ? 'text' : 'password'"
              input-class="brand_almost_black"
              name="password"
              bg-color="white"
              :placeholder="$t('login.loginForm.password.placeholder')"
              stack-label
              lazy-rules
              outlined
              @change="formAutofill = false"
              @animationstart="checkAutoFill"
              @blur="limitPass = true"
              @focus="limitPass = false"
              @keydown="isPasswordError = false"
            >
              <template v-slot:prepend>
                <q-icon class="uil uil-padlock login-input-icon" />
              </template>
              <template #append>
                <q-icon
                  v-show="password"
                  :data-testid="elementsIds.login.passwordEyeIcon"
                  class="icon-hover cursor-pointer"
                  :class="eyeIcon"
                  @click="showPass = !showPass"
                />
              </template>
            </q-input>
          </div>
          <incredi-note
            :data-testid="elementsIds.login.errorNote"
            v-if="getLoginError && !isAzureEnabled"
            type="Error"
            style="width: 308px; position: absolute; bottom: 56px; line-height: 16px"
            :style="uiVersion === 'beta' || uiVersion === 'main' ? 'bottom: 56px' : 'bottom: 48px'"
          >
            <span v-html="getLoginError" />
          </incredi-note>
        </div>

        <div
          class="login-buttons"
          :class="isAzureEnabled ? 'login-buttons-azure' : ''"
        >
          <div>
            <q-btn
              id="login-submit"
              ref="sumbit-button"
              flat
              type="submit"
              rounded
              no-caps
              tabindex="3"
              class="submit-button ibf-h4 incredi-button-ibx primary-strong"
              :label="$t('login.loginForm.submitLabel')"
            ></q-btn>
          </div>
          <div class="row justify-center items-center">
            <div
              id="forgot-password"
              class="gordita-400-11"
            >
              <a
                href
                @click.prevent="openForgotPasswordDialog"
              >
                {{ $t('login.loginForm.forgotPassword.text') }}
              </a>
            </div>
            <dot-divider
              :color="COLORS['Text/text_primary']"
              :font-size="'4px'"
              :margin="'0 12px'"
            />
            <div id="login-support-link">
              <a :href="supportMailUrl">{{ $t('login.loginForm.supportLink') }}</a>
            </div>
          </div>
          <div
            v-if="uiVersion === 'beta' || uiVersion === 'main'"
            id="beta_indicator"
            class="betaVersion"
          >
            {{ uiVersion }}_{{ uiVersionNum }}
          </div>
        </div>
      </div>
    </q-form>
    <div
      style="height: 70px; margin-bottom: 8px; width: 308px"
      :style="uiVersion === 'beta' || uiVersion === 'main' ? 'margin-top: 28px' : 'margin-top: 12px'"
    >
      <incredi-note
        :data-testid="elementsIds.login.errorNote"
        v-if="getLoginError && isAzureEnabled"
        type="Error"
        style=""
      >
        <span v-html="getLoginError" />
      </incredi-note>
    </div>
    <azure-login
      :is-connected="isAzureConnected"
      v-if="isAzureEnabled"
      style="margin-bottom: 16px"
    />
    <incredi-note
      :data-testid="elementsIds.login.errorNote"
      v-if="getAzureLoginError && isAzureEnabled"
      type="Error"
      style="width: 308px"
    >
      <span v-html="getAzureLoginError" />
    </incredi-note>
  </div>
</template>

<script>
import elementsIds from '@/const/elementIds.json';
import dotDivider from '@/components/common/dot-divider.vue';
import { inject, ref } from 'vue';
import { IKPlatformAuthentication } from '@/modules/platformAuthentication/platformAuthentication.injectionKeys';
import { IncrediNote } from 'incredi-ui';
import { ErrorCodes } from '@/const/errorCodes';
import COLORS from '@/design-system/colors.json';
import AzureLogin from '@/views/login/loginComponents/azure/azureLogin.vue';
import { useAuthentication_Get_Login_OptionsQuery } from '@/graphql/codegen/generated/graphql';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { config } from '@/config';

export default {
  name: 'LoginForm',
  components: { AzureLogin, dotDivider, IncrediNote },
  model: {
    events: ['submitLogin'],
  },
  props: {
    value: { type: Boolean, required: true },
  },
  setup() {
    const platformAuthentication = inject(IKPlatformAuthentication);
    const GQL_status = useAuthentication_Get_Login_OptionsQuery();
    return {
      formAutofill: ref(false),
      username: ref(''),
      password: ref(''),
      supportMailUrl: ref(process.env.VUE_APP_SUPPORT_MAIL),
      showPass: ref(false),
      text: ref(''),
      isUsernameError: ref(false),
      isPasswordError: ref(false),
      platformAuthentication,
      elementsIds,
      COLORS,
      GQL_status,
    };
  },
  computed: {
    getPlatformAuthentication() {
      return this.platformAuthentication?.authorization?.value?.GQL_result || null;
    },
    isAzureEnabled() {
      return this.GQL_status?.result?.value?.authentication_get_login_options?.azureAd?.enabled || false;
    },
    isAzureConnected() {
      return this.GQL_status?.result?.value?.authentication_get_login_options?.azureAd?.connected || false;
    },
    isLinux() {
      return this.GQL_status?.result?.value?.authentication_get_login_options?.osType === OsType.Linux;
    },
    isMac() {
      return this.GQL_status?.result?.value?.authentication_get_login_options?.osType === OsType.Mac;
    },
    getLoginError() {
      const messageVal = this.getPlatformAuthentication?.errors && this.getPlatformAuthentication?.errors[0]?.extensions?.applicationErrorCode;
      let errorMessage = null;
      switch (messageVal) {
        case ErrorCodes.INVALID_USER_NAME_OR_PASSWORD:
          errorMessage = this.$t('login.loginForm.validationCredentials.wrongUserPass');
          break;
        case ErrorCodes.REPEATED_INCORRECT_PASSWORD:
          errorMessage = this.$t('login.loginForm.validationCredentials.lockWarning');
          break;
        case ErrorCodes.USER_IS_LOCKED:
          errorMessage = this.$t('login.loginForm.validationCredentials.lockedUser');
          break;
        case ErrorCodes.USER_IS_INACTIVE:
          errorMessage = this.$t('login.loginForm.validationCredentials.inactiveUser');
          break;
        case ErrorCodes.USER_LDAP_REJECTED:
          errorMessage = this.$t('login.loginForm.validationCredentials.wrongUserPass');
          break;
        case ErrorCodes.LOCAL_USER_LOGIN_NOT_ALLOWED:
          errorMessage = this.$t('login.loginForm.validationCredentials.localUserNotAllowed');
          break;
        case ErrorCodes.INVALID_AUTHENTICATION_GROUP_MAPPING:
          errorMessage = this.$t('login.loginForm.validationCredentials.invalidAuthGroupMapping');
          break;
        case ErrorCodes.LDAP_MACHINE_IS_NOT_AVAILABLE:
          errorMessage = this.$t('login.loginForm.validationCredentials.ldapMachineIsDown');
          break;
        default:
          break;
      }
      return errorMessage;
    },
    getAzureLoginError() {
      let errorMessage = null;
      switch (this.platformAuthentication?.azureErrorCode.value) {
        case ErrorCodes.INVALID_AUTHENTICATION_GROUP_MAPPING:
          errorMessage = this.$t('login.loginForm.validationCredentials.invalidAuthGroupMapping');
          break;
        default:
          break;
      }
      return errorMessage;
    },
    uiVersion() {
      return process.env.VUE_APP_UI_VERSION;
    },
    uiVersionNum() {
      return process.env.VUE_APP_UI_VERSION_NUM;
    },
    loginFailed: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    eyeIcon() {
      if (this.showPass) return 'uil uil-eye';
      return 'uil uil-eye-slash';
    },
  },
  watch: {
    loginFailed() {
      const res = this.$refs['password-field'].validate();
      this.isUsernameError = res;
      this.isPasswordError = res;
      return res;
    },
  },
  methods: {
    submitLogin() {
      if (this.platformAuthentication) {
        this.platformAuthentication.azureErrorCode.value = null;
      }
      this.$emit('submitLogin', { username: this.username, password: this.password });
    },
    openForgotPasswordDialog() {
      let link = config?.value?.general?.appDocumentationUrl;
      if (this.isLinux) {
        link = config?.value?.general?.appDocumentationUrlLinux;
      } else if (this.isMac) {
        link = config?.value?.general?.appDocumentationUrlMac;
      }
      this.dialogPresets.forgotPassword(link);
    },
    checkAutoFill() {
      const formAutofill = { password: false, username: false };
      document.querySelectorAll('input:-webkit-autofill').forEach(el => {
        const autocomplete = el.getAttribute('autocomplete');
        if (autocomplete) formAutofill[autocomplete] = true;
      });
      if (Object.keys(formAutofill).every(key => formAutofill[key])) this.formAutofill = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.login-content {
  position: relative;
}
.betaVersion {
  font-size: 11px;
  position: relative;
  top: 20px;
  text-align: center;
  color: #03e8b4;
}
.login-input-field {
  margin: 5px 0;
  &.login-input-field-azure {
    margin-top: 32px !important;
  }
  :deep(.q-field__control) {
    display: flex;
    height: 50px;
    align-items: center;
    padding: 0 16px;
    border-radius: 2px;
    &:before {
      border-radius: 2px;
      border: 1px solid $-system-app-border;
    }
  }
  :deep(.q-field__bottom) {
    padding: 4px 0 0;
  }
  :deep(.q-field) {
    width: 308px;
  }
}
.icon-hover {
  color: $-text-text-medium;
  &:hover {
    background: $-hovers-hover-grey;
    transition: background 0.2s;
    border-radius: 50px;
  }
}
@import './commonLoginComponentsStyle';
.login-buttons {
  margin-top: 104px;
  &.login-buttons-azure {
    margin-top: 40px !important;
  }
}
.login-title {
  margin-bottom: 28px;
}
.login-subtitle {
  margin-bottom: 16px;
}
.q-field--error {
  :deep(.login-input-icon) {
    color: $-system-app-error;
  }
}
.q-field--focused {
  &:not(.q-field--error) {
    :deep(.login-input-icon) {
      color: $-text-text-primary;
    }
  }
}
.submit-button {
  margin-top: unset !important;
}
.q-field--with-bottom {
  padding-bottom: 0 !important;
}
</style>
